import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { TemplatePreview } from '@/components/templates/TemplatePreview';
import { useCreateRcsTemplate, useUpdateRcsTemplate } from '@/hooks/useRcs';
import type { RcsTemplate, RcsTemplateType } from '@/types/api.types';

// ── Schema ───────────────────────────────────────────────────────────────────

const schema = z
  .object({
    name: z.string().min(1, 'Nome obrigatório'),
    type: z.enum(['text', 'template']),
    isActive: z.boolean(),
    text: z.string(),
    zenviaTemplateId: z.string(),
    variables: z.array(z.object({ name: z.string().min(1), defaultValue: z.string() })),
  })
  .superRefine((val, ctx) => {
    if (val.type === 'text' && !val.text.trim()) {
      ctx.addIssue({ code: 'custom', path: ['text'], message: 'Conteúdo obrigatório' });
    }
    if (val.type === 'template' && !val.zenviaTemplateId.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['zenviaTemplateId'],
        message: 'ID do template Zenvia obrigatório',
      });
    }
  });

type Values = z.infer<typeof schema>;

// ── Content helpers ───────────────────────────────────────────────────────────

function parseContent(
  type: RcsTemplateType,
  content: unknown,
  zenviaTemplateId: string | null,
  entityVariables?: unknown,
): Partial<Values> {
  const c = (content ?? {}) as Record<string, unknown>;
  if (type === 'text') {
    return { text: (c['text'] as string) ?? '' };
  }
  const fields = (c['fields'] as Record<string, string> | null) ?? {};
  const fromFields = Object.entries(fields).map(([name, defaultValue]) => ({ name, defaultValue }));
  const fromEntity = Array.isArray(entityVariables)
    ? (entityVariables as string[]).map((name) => ({ name, defaultValue: '' }))
    : [];
  return {
    zenviaTemplateId: zenviaTemplateId ?? (c['templateId'] as string) ?? '',
    variables: fromFields.length > 0 ? fromFields : fromEntity,
  };
}

function buildContent(values: Values): unknown {
  if (values.type === 'text') {
    return { type: 'text', text: values.text };
  }
  return {
    type: 'template',
    templateId: values.zenviaTemplateId,
    fields: Object.fromEntries(
      values.variables.map(({ name, defaultValue }) => [name, defaultValue]),
    ),
  };
}

function getDefaultValues(template?: RcsTemplate | null): Values {
  const base: Values = {
    name: template?.name ?? '',
    type: template?.type ?? 'text',
    isActive: template?.isActive ?? true,
    text: '',
    zenviaTemplateId: template?.zenviaTemplateId ?? '',
    variables: [],
  };
  if (template) {
    return {
      ...base,
      ...parseContent(
        template.type,
        template.content,
        template.zenviaTemplateId,
        template.variables,
      ),
    };
  }
  return base;
}

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: RcsTemplate | null;
}

export function RcsTemplateFormDialog({ open, onOpenChange, template }: Props) {
  const isEdit = !!template;
  const { mutate: create, isPending: creating } = useCreateRcsTemplate();
  const { mutate: update, isPending: updating } = useUpdateRcsTemplate();
  const isPending = creating || updating;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(template),
  });

  const {
    fields: varFields,
    append: appendVar,
    remove: removeVar,
  } = useFieldArray({ control: form.control, name: 'variables' });

  const watchedType = form.watch('type');
  const watchedValues = form.watch();

  useEffect(() => {
    if (open) form.reset(getDefaultValues(template));
  }, [open, template]); // eslint-disable-line react-hooks/exhaustive-deps

  function detectVariables() {
    const text = form.getValues('text');
    const matches = [
      ...new Set(
        [...text.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]).filter((n): n is string => !!n),
      ),
    ];
    const existing = form.getValues('variables').map((v) => v.name);
    const toAdd = matches.filter((name) => !existing.includes(name));
    toAdd.forEach((name) => appendVar({ name, defaultValue: '' }));
  }

  function onSubmit(values: Values) {
    const payload = {
      name: values.name,
      type: values.type,
      content: buildContent(values),
      ...(values.type === 'template'
        ? { zenviaTemplateId: values.zenviaTemplateId || undefined }
        : {}),
    };

    if (isEdit && template) {
      update(
        { id: template.id, data: { ...payload, isActive: values.isActive } },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar template RCS' : 'Novo template RCS'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
            <div className="grid grid-cols-2 gap-6">
              {/* ── Left: form fields ── */}
              <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome</FormLabel>
                      <FormControl>
                        <Input placeholder="Boas-vindas" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <div className="flex flex-wrap gap-2">
                        {(['text', 'template'] as const).map((t) => (
                          <label
                            key={t}
                            className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                              field.value === t
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-input hover:bg-accent'
                            }`}
                          >
                            <input
                              type="radio"
                              className="sr-only"
                              value={t}
                              checked={field.value === t}
                              onChange={() => field.onChange(t)}
                            />
                            {{ text: 'Texto', template: 'Template' }[t]}
                          </label>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watchedType === 'text' && (
                  <>
                    <FormField
                      control={form.control}
                      name="text"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Conteúdo</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Olá, {{nome}}! Bem-vindo(a)."
                              rows={5}
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Use {`{{nome}}`} para inserir variáveis dinâmicas.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={detectVariables}>
                      <Search className="h-3 w-3" />
                      Detectar variáveis
                    </Button>
                    <VariablesSection fields={varFields} onRemove={removeVar} form={form} />
                  </>
                )}

                {watchedType === 'template' && (
                  <>
                    <FormField
                      control={form.control}
                      name="zenviaTemplateId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ID do template Zenvia (RCS)</FormLabel>
                          <FormControl>
                            <Input placeholder="template_id_zenvia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Variáveis</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => appendVar({ name: '', defaultValue: '' })}
                        >
                          <Plus className="h-3 w-3" />
                          Adicionar
                        </Button>
                      </div>
                      <VariablesSection fields={varFields} onRemove={removeVar} form={form} />
                    </div>
                  </>
                )}

                {isEdit && (
                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-3">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              id="rcs-tpl-isActive"
                            />
                          </FormControl>
                          <Label htmlFor="rcs-tpl-isActive">Ativo</Label>
                        </div>
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* ── Right: preview ── */}
              <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
                <TemplatePreview
                  type={watchedType}
                  text={watchedValues.text}
                  variables={watchedValues.variables}
                  zenviaTemplateId={watchedValues.zenviaTemplateId}
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Variables sub-section ────────────────────────────────────────────────────

interface VariablesSectionProps {
  fields: { id: string }[];
  onRemove: (index: number) => void;
  form: ReturnType<typeof useForm<Values>>;
}

function VariablesSection({ fields, onRemove, form }: VariablesSectionProps) {
  if (fields.length === 0) return null;

  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={f.id} className="flex items-start gap-2">
          <FormField
            control={form.control}
            name={`variables.${i}.name`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input placeholder="nome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${i}.defaultValue`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input placeholder="valor padrão" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive"
            onClick={() => onRemove(i)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
